import React, { createContext, useContext, useEffect, useCallback, useMemo, useState } from 'react';
import { useLocalStorage } from '@vastra/ui';
import type { ProductSummary, BlogSummary } from '@vastra/types';
import { deriveFacetsFromProducts, type Facets } from '@vastra/constants';
import { api } from '../services/api';

interface CatalogContextValue {
  products: ProductSummary[];
  blogs: BlogSummary[];
  loading: boolean;
  loadError: boolean;
  facets: Facets;
  recentProducts: ProductSummary[];
  trackRecentlyViewed: (productId: string) => void;
}

const CatalogContext = createContext<CatalogContextValue | null>(null);

export function useCatalog() {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error('useCatalog must be used within CatalogProvider');
  return ctx;
}

const RECENT_KEY = 'vastra_recently_viewed';
const MAX_RECENT = 12;

interface CatalogProviderProps {
  children: React.ReactNode;
}

export function CatalogProvider({ children }: CatalogProviderProps) {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [blogs, setBlogs] = useState<BlogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useLocalStorage<string[]>(RECENT_KEY, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [p, b] = await Promise.allSettled([api.getProducts(), api.getBlogs()]);
        if (cancelled) return;
        const gotProducts = p.status === 'fulfilled' && p.value.data;
        if (gotProducts) setProducts(p.value.data!);
        if (b.status === 'fulfilled' && b.value.data) setBlogs(b.value.data);
        if (!gotProducts) setLoadError(true);
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const facets = useMemo(() => deriveFacetsFromProducts(products), [products]);

  const trackRecentlyViewed = useCallback((productId: string) => {
    setRecentlyViewed((prev) => [productId, ...prev.filter((id) => id !== productId)].slice(0, MAX_RECENT));
  }, [setRecentlyViewed]);

  const recentProducts = useMemo(
    () => recentlyViewed.map((id) => products.find((p) => p.id === id)).filter(Boolean) as ProductSummary[],
    [recentlyViewed, products]
  );

  const value: CatalogContextValue = {
    products,
    blogs,
    loading,
    loadError,
    facets,
    recentProducts,
    trackRecentlyViewed,
  };

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}
